import * as React from 'react';
import { isPartialDate, partialDateToValue, sortPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import { Item } from '../../../../../components/Grid';
import HistoryRowControls from '../../../../../components/HistoryRowControls';
import HistoryRowListing from '../../../../../components/HistoryRowListing';
import HistoryRowSubHeader from '../../../../../components/HistoryRowSubHeader';
import { fm } from 'Components/FormatMessage';
import { categorizeTreatmentsByStatus, getLatestResMedDevice, isContinuous } from '../../../utils';
import { useAppSelector } from 'Store/index';
import Unit from 'Components/Unit';
import { path } from 'Utility/ramdaReplacement';
import { IHistoryContext } from 'Containers/FormContextHandler';
import { TDocTypes, TDocument } from '../../definitions';

/**
 * Treatment history headers that depend on treatment type and active diagnosis
 */
const TreatmentHistoryHeaders = ({
  type,
  category,
  hasContinuousOtherTreatments,
  hasResMedData,
  totalUsageHours,
}: {
  type: string;
  category?: 'active' | 'ended' | 'future';
  hasContinuousOtherTreatments?: boolean;
  hasResMedData?: boolean;
  totalUsageHours: string | undefined;
}): React.JSX.Element => {
  switch (type) {
    case 'patientDoesNotWantRespiratorySupportTherapy':
      return (
        <React.Fragment>
          <Item xs={3}>{fm('treatment.patientDoesNotWantRespiratorySupportTherapy.startDate')}</Item>
          <Item xs={3}>{fm('treatment.patientDoesNotWantRespiratorySupportTherapy.endDate')}</Item>
        </React.Fragment>
      );
    case 'oxygenTherapy':
      return (
        <React.Fragment>
          <Item xs={3}>{fm('treatment.startDate')}</Item>
          <Item xs={4}>{fm('treatment.endDate')}</Item>
          <Item xs={2}>{fm('treatment.oxygenTherapy.oxygenTherapyType')}</Item>
        </React.Fragment>
      );
    case 'hfncTherapy':
      return (
        <React.Fragment>
          <Item xs={3}>{fm('treatment.startDate')}</Item>
          <Item xs={4}>{fm('treatment.endDate')}</Item>
          <Item xs={2}>{fm('treatment.hfncTherapy.settings.HFNCTreatmentType')}</Item>
        </React.Fragment>
      );
    case 'papTherapy':
      if (hasResMedData) {
        if (category === 'future') return <Item xs={true} />;

        return (
          <React.Fragment>
            <Item xs={3}>{category === 'ended' ? fm('treatment.period') : fm('treatment.startDate')}</Item>
            <Item xs={2}>{fm('treatment.papTherapy.device')}</Item>
            <Item xs={2}>{fm('treatment.papTherapy.mode')}</Item>
            {totalUsageHours && <Item xs={2}>{fm('treatment.papTherapy.totalUsageHours')}</Item>}
          </React.Fragment>
        );
      }
      return (
        <React.Fragment>
          <Item xs={3}>{fm('treatment.startDate')}</Item>
          <Item xs={4}>{fm('treatment.endDate')}</Item>
          <Item xs={2}>{fm('treatment.papTherapy.device')}</Item>
        </React.Fragment>
      );
    case 'madTherapy':
      return (
        <React.Fragment>
          <Item xs={3}>{fm('treatment.startDate')}</Item>
          <Item xs={4}>{fm('treatment.endDate')}</Item>
          <Item xs={2}>{fm('treatment.latestControl')}</Item>
        </React.Fragment>
      );
    case 'surgicalTreatment':
      return (
        <React.Fragment>
          <Item xs={3}>{fm('treatment.surgicalTreatment.operationDate')}</Item>
          <Item xs={4}>{fm('treatment.surgicalTreatment.operationType')}</Item>
          <Item xs={2}>{fm('treatment.latestControl')}</Item>
        </React.Fragment>
      );
    case 'otherTreatment':
      return (
        <React.Fragment>
          <Item xs={3}>{fm('treatment.startDate')}</Item>
          {hasContinuousOtherTreatments ? <Item xs={2}>{fm('treatment.endDate')}</Item> : null}
          <Item xs={2}>{fm('treatment.treatmentType')}</Item>
          <Item xs={2}>{fm('treatment.madTherapy.controls.details')}</Item>
        </React.Fragment>
      );
    default:
      return <React.Fragment></React.Fragment>;
  }
};

/**
 * Treatment history items that depend on treatment type
 */
const TreatmentHistoryItems = ({
  d,
  type,
  category,
  hasResMedData,
  totalUsageHours,
  resMedDevice,
}: {
  d: TDocument;
  type: string;
  category?: 'active' | 'ended' | 'future';
  hasResMedData: boolean;
  totalUsageHours: string | undefined;
  resMedDevice: IPapDevice | undefined;
}): React.JSX.Element => {
  const hasTotalUsageHours = hasResMedData && totalUsageHours;
  switch (type) {
    case 'oxygenTherapy':
      return (
        <Item xs={2}>
          {path(['oxygenTherapyType'], d) ? fm(`treatment.oxygenTherapy.opts.${path(['oxygenTherapyType'], d)}`) : '-'}
        </Item>
      );
    case 'hfncTherapy':
      return (
        <Item xs={2} style={{ fontWeight: 600 }}>
          {path(['settings', 0, 'HFNCTreatmentType'], d)
            ? fm(`treatment.hfncTherapy.settings.opts.${path(['settings', 0, 'HFNCTreatmentType'], d)}`)
            : '-'}
        </Item>
      );
    case 'papTherapy': {
      const device =
        hasResMedData && resMedDevice
          ? (resMedDevice.deviceModel ?? '-')
          : path(['devices', 0, 'type'], d)
            ? fm(`treatment.papTherapy.opts.${path(['devices', 0, 'type'], d)}`)
            : '-';
      const mode = hasResMedData && resMedDevice ? (resMedDevice.mode ?? '-') : undefined;

      if (hasResMedData) {
        if (category === 'future') return <Item xs={true} />;

        return (
          <React.Fragment>
            <Item xs={2}>{device}</Item>
            <Item xs={2}>{mode}</Item>
            <Item xs={true}>
              {hasTotalUsageHours && (
                <Unit unit="h" fontWeight="bold">
                  {totalUsageHours}
                </Unit>
              )}
            </Item>
          </React.Fragment>
        );
      }
      return (
        <React.Fragment>
          <Item xs={2} />
          <Item xs={true}>{device}</Item>
        </React.Fragment>
      );
    }
    case 'surgicalTreatment': {
      const controls: Array<ISurgicalTreatmentControl> | undefined =
        path(['controlVisitsJawSurgery'], d) || path(['controlVisits'], d);
      const controlsSortedDescending = controls
        ? controls.toSorted((n1, n2) => sortPartialDate(n2.date, n1.date))
        : undefined;
      const latestGoneControl = controlsSortedDescending?.find(c => partialDateToValue(c.date) <= Date.now());
      return (
        <>
          <Item xs={4}>
            {path(['operationType'], d) ? fm(`treatment.surgicalTreatment.opts.${path(['operationType'], d)}`) : '-'}
          </Item>
          <Item xs={2}>{latestGoneControl ? formatPartialDate(latestGoneControl.date) : '-'}</Item>
        </>
      );
    }
    case 'otherTreatment':
      return (
        <React.Fragment>
          <Item xs={2}>
            {path(['otherTreatmentType'], d)
              ? fm(`treatment.otherTreatment.opts.${path(['otherTreatmentType'], d)}`)
              : '-'}
          </Item>
          <Item xs={true}>{path(['otherTreatmentDetails'], d) ? path(['otherTreatmentDetails'], d) : '-'}</Item>
        </React.Fragment>
      );
    case 'madTherapy': {
      const controls: Array<IMADControl> | undefined = path(['controls'], d);
      const controlsSortedDescending = controls
        ? controls.toSorted((n1, n2) => sortPartialDate(n2.date, n1.date))
        : undefined;
      const latestGoneControl = controlsSortedDescending?.find(c => partialDateToValue(c.date) <= Date.now());
      return <Item xs={2}>{latestGoneControl ? formatPartialDate(latestGoneControl.date) : '-'}</Item>;
    }
    default:
      return <Item />;
  }
};

/**
 * Common and treatment-specific history items combined
 */
const TreatmentHistory = ({
  d,
  startEdit,
  view,
  type,
  category,
  isContinuous,
  hasContinuousOtherTreatments,
  hasResMedData,
  totalUsageHours,
  resMedDevice,
}: {
  d: TDocument;
  startEdit: ITreatmentHistoryProps['startEdit'];
  view?: ITreatmentHistoryProps['view'];
  type: string;
  category?: 'active' | 'ended' | 'future';
  isContinuous: (d: TDocument) => boolean;
  hasContinuousOtherTreatments: boolean;
  hasResMedData: boolean;
  totalUsageHours: string | undefined;
  resMedDevice: IPapDevice | undefined;
}): React.JSX.Element => {
  const endDateSize =
    type === 'papTherapy' || (type === 'otherTreatment' && hasContinuousOtherTreatments)
      ? 2
      : type === 'patientDoesNotWantRespiratorySupportTherapy'
        ? true
        : 4;

  const showPeriod =
    type === 'papTherapy' && hasResMedData && category === 'ended' && d.date && 'endDate' in d && d.endDate;

  return (
    <React.Fragment>
      <Item xs={3} style={{ fontWeight: 600 }}>
        {showPeriod
          ? `${formatPartialDate(d.date)} – ${formatPartialDate(d.endDate)}`
          : d.date
            ? formatPartialDate(d.date)
            : '-'}
      </Item>
      {(!hasResMedData || type !== 'papTherapy') && (isContinuous(d) || hasContinuousOtherTreatments) ? (
        <Item xs={endDateSize} style={{ fontWeight: 600 }}>
          {isPartialDate(path(['endDate'], d)) ? formatPartialDate(path(['endDate'], d)) : isContinuous(d) ? '-' : ''}
        </Item>
      ) : null}
      <TreatmentHistoryItems
        d={d}
        type={type}
        category={category}
        hasResMedData={hasResMedData}
        totalUsageHours={totalUsageHours}
        resMedDevice={resMedDevice}
      />
      <Item xs={3}>{startEdit ? <HistoryRowControls document={d} startEdit={startEdit} view={view} /> : null}</Item>
    </React.Fragment>
  );
};

/**
 * Component for rendering same looking history section for each of the treatments
 */
export const TreatmentHistorySection = ({
  documents,
  startEdit,
  type,
  view,
}: ITreatmentHistoryProps): React.JSX.Element => {
  const docs = documents.filter((d: any) => d._type === type) as TDocument[];

  /**
   * Check if patient has continuous other treatments which are exclusive to sleep apnea platform
   * This is used to correctly format the history view between sleep apnea and respiratory failure diagnoses
   */
  const hasContinuousOtherTreatments = docs?.some(
    (d: TDocument) =>
      d._type === 'otherTreatment' &&
      [undefined, 'postureTreatment', 'otherTreatment'].includes(path(['otherTreatmentType'], d)),
  );

  const treatmentsByStatus = categorizeTreatmentsByStatus(docs, type);

  const activeDocs = treatmentsByStatus.active;
  const endedDocs = treatmentsByStatus.ended;
  const futureDocs = treatmentsByStatus.future;
  const notStartedDocs = treatmentsByStatus.notStarted;

  const resMedLink = useAppSelector((s: IState) => s.session.patientIntegrationDetails?.resMed);
  const resMedIntegrationEnabled = !!resMedLink;
  const totalUsageHours = resMedLink?.totalUsageHours;

  const readonlyDocuments = useAppSelector((s: IState) => s.readonly.documents);
  const resMedDevices = readonlyDocuments.filter(d => d._type === 'resMedDevice') as IPapDevice[];
  const resMedDevice = Array.isArray(resMedDevices) && resMedDevices.length > 0 ? resMedDevices[0] : undefined;

  const hasResMedData = resMedIntegrationEnabled && Boolean(resMedDevice);

  const getResMedProps = (category: 'active' | 'ended' | 'future', d?: TDocument) => ({
    totalUsageHours: category === 'active' ? totalUsageHours : undefined,
    resMedDevice: d ? getLatestResMedDevice(readonlyDocuments, d) : resMedDevice,
  });

  return (
    <React.Fragment>
      {/** Render active treatments here */}
      {activeDocs.length > 0 && (
        <React.Fragment>
          {type !== 'patientDoesNotWantRespiratorySupportTherapy' && (
            <HistoryRowSubHeader header={fm('treatment.activeTreatments')} fontSize="small" />
          )}
          <HistoryRowListing
            documents={activeDocs}
            headers={
              <TreatmentHistoryHeaders
                type={type}
                category="active"
                hasContinuousOtherTreatments={hasContinuousOtherTreatments}
                hasResMedData={hasResMedData}
                {...getResMedProps('active')}
              />
            }
            contentFormat={(d: TDocument): React.JSX.Element => (
              <TreatmentHistory
                d={d}
                startEdit={startEdit}
                type={type}
                category="active"
                view={view}
                isContinuous={isContinuous}
                hasContinuousOtherTreatments={hasContinuousOtherTreatments}
                hasResMedData={hasResMedData}
                {...getResMedProps('active', d)}
              />
            )}
          />
        </React.Fragment>
      )}
      {/** Render ended treatments here */}
      {endedDocs.length > 0 && (
        <React.Fragment>
          <HistoryRowSubHeader
            header={
              type === 'surgicalTreatment' || type === 'otherTreatment'
                ? fm('treatment.previousTreatments')
                : fm('treatment.endedTreatments')
            }
            fontSize="small"
          />
          <HistoryRowListing
            documents={endedDocs}
            headers={
              <TreatmentHistoryHeaders
                type={type}
                category="ended"
                hasContinuousOtherTreatments={hasContinuousOtherTreatments}
                hasResMedData={hasResMedData}
                {...getResMedProps('ended')}
              />
            }
            contentFormat={(d: TDocument): React.JSX.Element => (
              <TreatmentHistory
                d={d}
                startEdit={startEdit}
                view={view}
                type={type}
                category="ended"
                isContinuous={isContinuous}
                hasContinuousOtherTreatments={hasContinuousOtherTreatments}
                hasResMedData={hasResMedData}
                {...getResMedProps('ended', d)}
              />
            )}
          />
        </React.Fragment>
      )}
      {/** Render future treatments here */}
      {futureDocs.length > 0 && (
        <React.Fragment>
          <HistoryRowSubHeader header={fm('treatment.upcomingTreatments')} fontSize="small" />
          <HistoryRowListing
            documents={futureDocs}
            headers={
              <TreatmentHistoryHeaders
                type={type}
                category="future"
                hasContinuousOtherTreatments={hasContinuousOtherTreatments}
                hasResMedData={hasResMedData}
                {...getResMedProps('future')}
              />
            }
            contentFormat={(d: TDocument): React.JSX.Element => (
              <TreatmentHistory
                d={d}
                startEdit={startEdit}
                type={type}
                category="future"
                view={view}
                isContinuous={isContinuous}
                hasContinuousOtherTreatments={hasContinuousOtherTreatments}
                hasResMedData={hasResMedData}
                {...getResMedProps('future', d)}
              />
            )}
          />
        </React.Fragment>
      )}
      {/** Render not started treatments here */}
      {notStartedDocs.length > 0 && (
        <React.Fragment>
          <HistoryRowSubHeader header={fm('treatment.madTherapy.treatmentNotStarted')} fontSize="small" />
          <HistoryRowListing
            documents={notStartedDocs}
            headers={
              <React.Fragment>
                <Item xs={3}>{fm('treatment.madTherapy.treatmentDecisionDate')}</Item>
                <Item xs={3}>{fm('treatment.madTherapy.reasonForNotStartingTreatment')}</Item>
                <Item xs={6} />
              </React.Fragment>
            }
            contentFormat={(d: TDocument): React.JSX.Element => (
              <React.Fragment>
                <Item xs={3}>{'treatmentDecisionDate' in d ? formatPartialDate(d.treatmentDecisionDate) : '-'}</Item>
                <Item xs={3}>
                  {((d as IMADTherapy).reasonForNotStartingTreatment || []).length > 0
                    ? ((d as IMADTherapy).reasonForNotStartingTreatment || []).map((r: string, i: number) => (
                        <span key={r}>
                          {i !== 0 && ', '}
                          {fm('treatment.madTherapy.opts.' + r)}
                        </span>
                      ))
                    : '-'}
                </Item>
                <Item xs={3} />
                <Item xs={3}>
                  {startEdit ? <HistoryRowControls document={d} startEdit={startEdit} view={view} /> : null}
                </Item>
              </React.Fragment>
            )}
          />
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

interface ITreatmentHistoryProps {
  documents: Array<TDocument>;
  startEdit: IHistoryContext<IPAPTherapy>['startEdit'];
  type: TDocTypes;
  view: IHistoryContext<IPAPTherapy>['view'];
}
